<template>
  <div
    data-testid="pagination"
    class="pagination flex w-full flex-col items-center justify-between gap-2 px-7 py-4 md:flex-row"
  >
    <div
      class="flex w-full max-w-sm items-center justify-between gap-3 md:w-fit md:justify-normal"
    >
      <span class="font-bold">Rows per page</span>
      <select
        data-testid="pagination-page-size-select"
        class="appearance-none rounded border !border-p-500 bg-transparent py-1 pl-2 pr-7 font-bold outline-none focus:border-transparent focus:ring-p-500"
        :value="rowPerPage"
        @change="changeRowPerPage"
      >
        <option v-for="item in rowsPerPageList" :key="item" :value="item">
          {{ item }}
        </option>
        <option :value="total"> All </option>
      </select>
    </div>

    <div
      class="flex w-full max-w-sm items-center justify-between gap-2 md:w-fit md:justify-normal"
    >
      <ChevronFirst
        data-testid="pagination-first-page"
        :class="allowedClasses(currentPage !== 1)"
        @click="onFirstPage"
      />
      <ChevronLeft
        data-testid="pagination-previous-page"
        :class="allowedClasses(currentPage !== 1)"
        @click="onPreviousPage"
      />
      <div class="flex items-center gap-4">
        <span
          :data-testid="`pagination-page-${currentPage}`"
          class="flex h-7 w-7 select-none items-center justify-center rounded border border-p-500 font-bold"
        >
          {{ currentPage }}
        </span>
        <span class="font-bold">of {{ pages }}</span>
      </div>
      <ChevronRight
        data-testid="pagination-next-page"
        :class="allowedClasses(currentPage !== pages)"
        @click="onNextPage"
      />
      <ChevronLast
        data-testid="pagination-last-page"
        :class="allowedClasses(currentPage !== pages)"
        @click="onLastPage"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import ChevronRight from '~/assets/icons/angle-right.svg'
import ChevronLeft from '~/assets/icons/chevron-left-black.svg'
import ChevronLast from '~/assets/icons/chevron-last.svg'
import ChevronFirst from '~/assets/icons/chevron-first.svg'

const props = defineProps<{
  currentPage: number
  pages: number
  rowPerPage: number
  rowsPerPageList: number[]
  total: number
}>()

const emit = defineEmits(['changePage', 'changeRowPerPage'])

const allowedClasses = (isAllowed: boolean) => {
  return isAllowed ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
}

const onPreviousPage = () => {
  if (props.currentPage > 1) emit('changePage', props.currentPage - 1)
}

const onNextPage = () => {
  if (props.currentPage < props.pages) emit('changePage', props.currentPage + 1)
}

const onFirstPage = () => {
  if (props.currentPage !== 1) emit('changePage', 1)
}

const onLastPage = () => {
  if (props.currentPage !== props.pages) emit('changePage', props.pages)
}

const changeRowPerPage = (event: Event) => {
  const target = event.target as HTMLSelectElement
  emit('changeRowPerPage', Number(target.value))
}
</script>

<style scoped>
.pagination {
  background: #fff;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
}

.pagination option {
  background: #fff;
}
</style>
